import { parsePhoneNumber } from "libphonenumber-js"

import { validate as validateEmail } from "email-validator"
import type { TComposerMode } from "@/components/generic/TemplatesComposer2/types"
import { TInteraction } from "@/schemas/contacts"

export function prettyDuration(duration?: number | null): string {
  // duration must be in seconds
  const _duration = duration ?? 0
  const hours = Math.floor(_duration / 3600)
  const minutes = Math.floor((_duration % 3600) / 60)
  const seconds = _duration % 60

  const durationArr = []
  if (hours > 0) durationArr.push(`${hours}h`)
  if (minutes > 0) durationArr.push(`${minutes}m`)
  if (seconds > 0) durationArr.push(`${seconds}s`)

  return durationArr.join(" ")
}

export interface TPrettyInteractionData {
  type: "message" | "call" | "email" | "unknown" | "video"
  direction: "incoming" | "outgoing" | "none"
  outcome:
    | "voicemail"
    | "missed"
    | "failed"
    | "cancelled"
    | "success"
    | "unknown"
}

export function extractPrettyInteractionData(
  interaction?: TInteraction
): TPrettyInteractionData {
  let _direction: "incoming" | "outgoing" | "none" = "none"

  if (interaction?.Direction === "in") {
    _direction = "incoming"
  } else if (interaction?.Direction === "ou") {
    _direction = "outgoing"
  }

  let _type: "message" | "call" | "email" | "video" | "unknown" = "unknown"
  let _outcome:
    | "voicemail"
    | "missed"
    | "failed"
    | "cancelled"
    | "success"
    | "unknown" = "unknown"

  switch (interaction?.Type) {
    case "message": {
      _type = "message"
      break
    }
    case "departmentCall":
    case "personalCall":
      _type = "call"
      break
    case "video": {
      _type = "video"
      break
    }

    default:
      _type = "unknown"
      break
  }

  const _rawOutcome = interaction?.Outcome ?? interaction?.Status ?? "unknown"

  switch (_rawOutcome) {
    case "vo": // in Outcome
      _outcome = "voicemail"
      break
    case "mi": // in Outcome
      _outcome = "missed"
      break
    case "undelivered": // in Status
    case "canceled": {
      // in Status, for video
      _outcome = "cancelled"
      break
    }
    case "failed":
      _outcome = "failed"
      break
    case "cm": // in Outcome (not sure what does it mean)
    case "success":
      _outcome = "success"
      break
    default:
      _outcome = "unknown"
      break
  }

  return {
    type: _type,
    direction: _direction,
    outcome: _outcome,
  }
}

/**
 * Extract and prettify the contact data based on the provided parameters.
 *
 * @param {Object} options - The options object.
 * @param {string | Array<string>} options.emails - The email(s) associated with the contact.
 * @param {string} options.email - The primary email associated with the contact.
 * @param {string | Array<string>} options.phoneNumbers - The phone number(s) associated with the contact.
 * @param {string} options.phoneNumber - The primary phone number associated with the contact.
 * @param {string} options.fullName - The full name of the contact.
 * @param {string} options.fallback - The fallback name to use if the full name is not provided.
 * @param {"sms" | "email"} options.mode - The mode to determine how to prettify the contact name.
 * @param {boolean} options.showInvalidValues - Whether to show invalid values.
 * @returns {Object} The prettifed contact data.
 */
export function extractPrettyContactData({
  email,
  emails,
  fullName,
  phoneNumber,
  phoneNumbers,
  fallback,
  mode,
  showInvalidValues,
}: {
  emails?: string | Array<string> | null
  email?: string | null
  phoneNumbers?: string | Array<string> | null
  phoneNumber?: string | null
  fullName?: string | null
  fallback?: string
  mode: TComposerMode
  showInvalidValues?: boolean
}): {
  contactEmail: string
  contactPhoneNumber: string
  contactFullName: string
} {
  // Extract the email
  let contactEmail: string = ""
  const _contactEmails: Array<string> =
    typeof emails === "string" ? (emails ?? "").split(",") : (emails ?? [])

  if ((email ?? "").trim() !== "") {
    contactEmail = (email ?? "").trim()
  } else if (_contactEmails.length > 0 && _contactEmails[0].trim() !== "") {
    contactEmail = _contactEmails[0].trim()
  }

  // Extract the phone
  let contactPhoneNumber: string = ""

  const _contactPhoneNumbers: Array<string> =
    typeof phoneNumbers === "string"
      ? (phoneNumbers ?? "").split(",")
      : (phoneNumbers ?? [])

  if ((phoneNumber ?? "").trim() !== "") {
    contactPhoneNumber = (phoneNumber ?? "").trim()
  } else if (
    _contactPhoneNumbers.length > 0 &&
    _contactPhoneNumbers[0].trim() !== ""
  ) {
    contactPhoneNumber = _contactPhoneNumbers[0].trim()
  }

  // Extract the full name
  let contactFullName = fallback ?? "Unknown name"

  if (fullName && fullName?.trim() !== "") {
    contactFullName = fullName.trim()
  } else {
    switch (mode) {
      case "sms":
        if (contactPhoneNumber) {
          contactFullName = prettifyPhone({
            phone: contactPhoneNumber,
            showInvalidValues,
          })
        }
        break
      case "email":
      default:
        if (contactEmail) {
          contactFullName = contactEmail
        }
        break
    }
  }

  return {
    contactEmail,
    contactPhoneNumber,
    contactFullName,
  }
}

/**
 * Takes in a phone number and an optional contact name and returns a formatted string
 * with the contact name and the phone number in either national or international format.
 *
 * If the phone number is invalid, the function will return an empty string unless showInvalidValues is true.
 * @param {string | undefined} phone - the phone number to format
 * @param {string | undefined} contactName - the name of the contact associated with the phone number
 * @param {boolean | undefined} showInvalidValues - whether to show invalid phone numbers
 * @returns {string} - the formatted string with the contact name and phone number
 * @throws {Error} - if no phone number is provided
 */
export function prettifyPhone({
  contactName,
  phone,
  showInvalidValues,
}: {
  phone?: string
  contactName?: string
  showInvalidValues?: boolean
}): string {
  let ret = ""
  try {
    if (!phone) return ""

    const phoneNumber = parsePhoneNumber(phone)

    if (!phoneNumber?.isValid()) {
      if (showInvalidValues) {
        return contactName ? `${contactName} (${phone})` : phone
      } else {
        return ""
      }
    }

    const isUSPhone = phoneNumber.getPossibleCountries().includes("US")

    if (isUSPhone) {
      ret = contactName
        ? `${contactName} (${phoneNumber.formatNational()})`
        : phoneNumber.formatNational()
    } else {
      ret = contactName
        ? `${contactName} (${phoneNumber.formatInternational()})`
        : phoneNumber.formatInternational()
    }
  } catch (err) {
    console.error(err)
    ret = contactName ?? "Unknown"
  }
  return ret
}

/**
 * Takes in an email and a contact name and returns a prettified version of the email.
 * If a contact name is provided, the email will be formatted as "contactName (email)".
 * If no email is provided, an error will be thrown and the contact name will be returned.
 * @param {string | undefined} email - the email to prettify
 * @param {string | undefined} contactName - the name of the contact associated with the email
 * @returns {string} - the prettified email
 */
export function prettifyEmail(email?: string, contactName?: string): string {
  let ret = ""
  try {
    if (email && validateEmail(email)) {
      ret = contactName ? `${contactName} (${email})` : email
    }
  } catch (err) {
    console.error(err)
    ret = contactName ?? "Unknown"
  }

  return ret
}
